import type { CraftSelection } from '@/shared/pinia/modules/user';
import { contextualError } from '@/shared/utils/error';

const MARKETING_SIGNUP_PATH = '/apps/cognito/api/marketing-signup';

export const marketingSignupSources = {
  PATTERN_DOWNLOAD: 'pattern_download'
} as const;

export type MarketingSignupSource =
  (typeof marketingSignupSources)[keyof typeof marketingSignupSources];

const CRAFT_WIRE_NAME: Record<CraftSelection['name'], string> = {
  Knit: 'knitting',
  Crochet: 'crochet'
};

const SKILL_LEVEL_WIRE_NAME: Record<CraftSelection['skillLevel'], string> = {
  Beginner: 'beginner',
  Intermediate: 'intermediate',
  Experienced: 'experienced'
};

export const toWireCraftSkillLevels = (crafts: CraftSelection[]) =>
  crafts.map(({ name, skillLevel }) => ({
    craft: CRAFT_WIRE_NAME[name],
    skillLevel: SKILL_LEVEL_WIRE_NAME[skillLevel]
  }));

export type MarketingSignupOrigin = {
  // Sourced from Liquid's `localization` object, never computed client-side —
  // one Shopify store serves many markets, so the shop domain alone can't
  // say which storefront locale actually rendered the form.
  language?: string;
  country?: string;
  market?: string;
  referrer?: string;
};

export type MarketingSignupPayload = {
  email: string;
  name?: string;
  source: MarketingSignupSource;
  subscribed: boolean;
  craftSkillLevels?: { craft: string; skillLevel: string }[];
  origin?: MarketingSignupOrigin;
  // One id per submission, minted where the submission is stashed and never
  // persisted. Klaviyo deduplicates on (profile, metric, unique_id), so a value
  // that repeated across submissions would have a returning customer's second
  // declaration silently discarded as a duplicate of their first.
  event_id: string;
  // Bot-protection fields MarketingSignUpRequest reads outside its validated
  // rules: `website` must stay empty (honeypot) and `formFilledInMs` must be
  // at least 800ms, or the submission is silently accepted and discarded.
  website: string;
  formFilledInMs: number;
};

export type MarketingSignupResponse = {
  success: boolean;
  // The person key, minted server-side in Cognito App's `users` table and
  // returned here rather than set as a cookie: Shopify strips `Set-Cookie` from
  // an app proxy response, so the storefront has to persist it. Optional because
  // the key is absent, not null, when the submission resolved no identity.
  hobbii_cognito_uuid?: string;
};

export const submitMarketingSignup = async (
  payload: MarketingSignupPayload
) => {
  const response = await fetch(MARKETING_SIGNUP_PATH, {
    method: 'POST',
    cache: 'no-store',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    throw contextualError(
      'MarketingSignupError',
      new Error(`HTTP ${response.status}`),
      {
        message: 'Marketing signup submission failed',
        data: { source: payload.source }
      }
    );
  }

  return response.json() as Promise<MarketingSignupResponse>;
};
